import {Component, Input, OnInit} from '@angular/core';
import {IncidentTransaction, IncidentType, ReferenceBookType} from '@npck/af-frontend-api';
import {forkJoin} from 'rxjs';
import {mapToViewModel} from '../../../../../../../components/files/file-preview/file-preview.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../../../domain/dictionary-holder.service';
import {
    Dictionaries,
    FieldBuilderData,
    FieldBuilderService,
    FieldInfo,
    PARTICIPANT_FIELD_NAMES,
    PARTICIPANT_NESTED_FIELD,
} from '../../../../../../../domain/field-builder.service';
import {AttachmentViewModel} from '../../../../documents-tab/documents-tab.component';

@Component({
    selector: 'af-transaction-details-tab',
    templateUrl: 'incident-transaction-details-tab.component.html',
    styleUrls: ['incident-transaction-details-tab.component.scss'],
    standalone: false,
})
export class IncidentTransactionDetailsTabComponent implements OnInit {
    @Input() transaction: IncidentTransaction;
    @Input() incidentTypeCode: string;

    commonFields: FieldInfo[];
    senderFields: FieldInfo[];
    receiverFields: FieldInfo[];
    attachments: AttachmentViewModel[] = [];

    commonFieldNames: (keyof IncidentTransaction)[] = [
        'fraudScheme',
        'channel',
        'type',
        'date',
        'amount',
        'transactionRrn',
        'payment_id',
        'clientConfirmed',
        'confirmationMethod',
        'clientLegitimacyConfirmation',
        'status'
    ];

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private fieldBuilderService: FieldBuilderService,
    ) {
    }

    getButtonTranslationKey = (): string => {
        return [...this.senderFields ?? [], ...this.receiverFields ?? []].some(field => field.isExpanded)
            ? 'i18n_af_buttonCollapseAll'
            : 'i18n_af_buttonExpandAll';
    };

    ngOnInit(): void {
        if (this.incidentTypeCode !== IncidentType.FRAUD) {
            this.commonFieldNames = this.commonFieldNames.filter(fieldName =>
                fieldName !== 'fraudScheme' && fieldName !== 'channel'
            );
        }

        if (!this.transaction.clientConfirmed) {
            this.commonFieldNames = this.commonFieldNames.filter(fieldName =>
                fieldName !== 'confirmationMethod' && fieldName !== 'clientLegitimacyConfirmation'
            );
        }

        forkJoin([
            this.dictionaryHolderService.getReference('incident_role'),
            this.dictionaryHolderService.getReference('bin'),
            this.dictionaryHolderService.getReference(ReferenceBookType.ATTACHED_DOC),
            this.dictionaryHolderService.getReference('confirmation_method'),
            this.dictionaryHolderService.getReference('fraud_scheme'),
            this.dictionaryHolderService.getReference('transaction_channel'),
            this.dictionaryHolderService.getReference('account_status'),
        ])
            .subscribe(([roles, bins, docTypes, confirmationMethods, fraudSchemes, channels, accountStatuses]) => {
                const dictionaries: Dictionaries = {roles, bins, confirmationMethods, accountStatuses};
                this.commonFields = this.fieldBuilderService.buildFields(<FieldBuilderData>{
                    fieldNames: this.commonFieldNames,
                    data: this.transaction,
                    dictionaries: dictionaries,
                    customValueMapper: {
                        fraudScheme: value => findValueInDictionary(fraudSchemes, value, 'name_ru'),
                        channel: value => findValueInDictionary(channels, value, 'name_ru'),
                    },
                });
                this.senderFields = this.transaction.sender
                    ? this.fieldBuilderService.buildFields(<FieldBuilderData>{
                        fieldNames: PARTICIPANT_FIELD_NAMES,
                        nestedFieldNames: PARTICIPANT_NESTED_FIELD,
                        data: this.transaction.sender,
                        dictionaries: dictionaries,
                        context: 'participant',
                        type: this.transaction.sender.type,
                    }) : [];
                this.receiverFields = this.fieldBuilderService.buildFields(<FieldBuilderData>{
                    fieldNames: PARTICIPANT_FIELD_NAMES,
                    nestedFieldNames: PARTICIPANT_NESTED_FIELD,
                    data: this.transaction.receiver,
                    dictionaries: dictionaries,
                    context: 'participant',
                    type: this.transaction.receiver.type,
                });
                this.attachments = (this.transaction.attachments ?? [])
                    .map(attachment => mapToViewModel(attachment, docTypes))
                    .filter(a => a.files?.length > 0);
            });
    }

    toggleFields(): void {
        [...this.senderFields, ...this.receiverFields].forEach((field: FieldInfo) => {
            field.isExpanded = !field.isExpanded;
        });
    }
}
